import React, { useState, useEffect, useMemo } from "react";
import { Alert, Avatar, Box, Button, Chip, Collapse, Divider, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import EmployeeCorrectionDialog from "./EmployeeCorrectionDialog";
import PendingShiftsPanel from "./PendingShiftsPanel";
import { LoadingButton } from "@mui/lab";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HistoryIcon from "@mui/icons-material/History";
import { DialogAnimate } from "src/components/Animate";
import { DialogContent } from "@material-ui/core";
import { useDispatch, useSelector } from "src/redux/store";
import { useClaims } from "src/hooks/useClaims";
import {
  changeOpenClockingModal,
  updateClockingSuccess,
  selectClockingByIdSuccess,
  openKiosk,
  GetAllEmployees,
  getCurrentClockingSuccess,
  setKioskReturnAfterClocking,
} from "src/redux/slices/employee";
import { Tab, Tabs } from "@mui/material";
import { useSnackbar } from "notistack";
import axios from "src/utils/axios";
import { fDateTime, fDateTimeZ, fTimeSpanToText } from "src/utils/formatTime";
import MyAutoSnack from "src/components/MyAutoSnack";
import createAvatar from "src/utils/createAvatar";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/es";
import { buildShiftAction, clockingDeviceLabel } from "src/utils/clocking/clockingDevice";

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.locale("es");

// Backend serializa start/end/checkInTime/checkOutTime como wall-clock UTC
// SIN sufijo 'Z' (EF Core pierde DateTimeKind al leer de SQL → Unspecified).
// Sin esta normalización, dayjs los interpretaba como local y restaba 2h en
// CEST (Madrid en verano): un turno programado 13:00-22:00 se mostraba como
// 11:00-20:00.
const fromUtc = (d) => (d ? dayjs.utc(d).local() : null);

const EventoTipo = Object.freeze({
  START_SHIFT: 0,
  STOP_SHIFT: 1,
  START_BREAK: 2,
  STOP_BREAK: 3,
});

const MODO_META = {
  inactivo:  { label: "Sin turno",    color: "#64748b", bg: "rgba(100,116,139,0.1)"  },
  trabajando:{ label: "En turno",     color: "#16a34a", bg: "rgba(22,163,74,0.1)"    },
  descanso:  { label: "En descanso",  color: "#d97706", bg: "rgba(217,119,6,0.1)"    },
};

// ── Timeline de una jornada ───────────────────────────────────────────────────
function JornadaTimeline({ jornada }) {
  const [expanded, setExpanded] = useState(false);
  const checkIn = fromUtc(jornada.checkInTime);
  const checkOut = fromUtc(jornada.checkOutTime);
  const duracion = checkOut
    ? (() => {
        const t = dayjs.duration(checkOut.diff(checkIn));
        return `${t.hours()}h ${t.minutes()}m`;
      })()
    : "En curso";

  const isActive = !checkOut;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 1.5, py: 1, borderRadius: 2, cursor: "pointer",
          bgcolor: isActive ? "rgba(22,163,74,0.07)" : "rgba(99,102,241,0.05)",
          border: isActive ? "1px solid rgba(22,163,74,0.15)" : "1px solid transparent",
          "&:hover": { bgcolor: isActive ? "rgba(22,163,74,0.12)" : "rgba(99,102,241,0.09)" },
          transition: "background 0.15s",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isActive && (
            <FiberManualRecordIcon sx={{ fontSize: 8, color: "#16a34a" }} />
          )}
          <Typography variant="caption" fontWeight={600} sx={{ color: "text.primary", fontSize: 11 }}>
            {checkIn?.format("HH:mm")}
            {checkOut && ` → ${checkOut.format("HH:mm")}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Chip indicativo del ORIGEN del cierre: si la jornada tiene
              `checkOutSource` (viene del enlace del email), pintamos un
              chip lila con el modo usado. Sin este visual, cierres
              presenciales (TPV) y remotos (email) se veían idénticos y
              no se podía auditar de un vistazo. */}
          {jornada.checkOutSource && (
            <Chip
              label={
                jornada.checkOutSource === "email-link-scheduled"
                  ? "Cerrado por email (h. prevista)"
                  : jornada.checkOutSource === "email-link-manual"
                    ? "Cerrado por email (h. manual)"
                    : "Cerrado por email"
              }
              size="small"
              sx={{
                fontSize: 9, height: 18,
                bgcolor: "rgba(147,51,234,0.12)",
                color: "#7e22ce",
                fontWeight: 700,
              }}
            />
          )}
          <Chip
            label={duracion}
            size="small"
            sx={{
              fontSize: 10, height: 18,
              bgcolor: isActive ? "rgba(22,163,74,0.12)" : "rgba(99,102,241,0.10)",
              color: isActive ? "#16a34a" : "primary.main",
              fontWeight: 700,
            }}
          />
          <ExpandMoreIcon sx={{ fontSize: 14, color: "text.secondary", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 2, pt: 0.5, pb: 0.5 }}>
          <TimelineRow icon="▶" color="#16a34a" label="Inicio" device={clockingDeviceLabel(jornada.deviceName, jornada.deviceIdentifier)} time={checkIn?.format("HH:mm")} />
          {jornada.breaks?.map((b, i) => (
            <React.Fragment key={i}>
              <TimelineRow icon="⏸" color="#d97706" label={b.duration ? `Descanso (${fTimeSpanToText(b.duration)})` : "Inicio descanso"} device={clockingDeviceLabel(b.deviceName, b.deviceIdentifier)} time={fromUtc(b.breakStart)?.format("HH:mm")} />
              {b.breakEnd && <TimelineRow icon="▶" color="#16a34a" label="Fin descanso" time={fromUtc(b.breakEnd)?.format("HH:mm")} />}
            </React.Fragment>
          ))}
          {checkOut && <TimelineRow icon="■" color="#ef4444" label="Fin" device={clockingDeviceLabel(jornada.checkOutDeviceName, jornada.checkOutDeviceIdentifier)} time={checkOut.format("HH:mm")} />}
          {jornada.checkOutSource && jornada.checkOutEditedAt && (
            <TimelineRow
              icon="✎"
              color="#7e22ce"
              label={
                jornada.checkOutSource === "email-link-scheduled"
                  ? "Cerrado por email (hora prevista)"
                  : jornada.checkOutSource === "email-link-manual"
                    ? "Cerrado por email (hora manual)"
                    : "Cerrado por email"
              }
              time={fromUtc(jornada.checkOutEditedAt)?.format("DD/MM HH:mm")}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function TimelineRow({ icon, color, label, time, device }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4 }}>
      <Box sx={{ width: 16, textAlign: "center", fontSize: 9, color, flexShrink: 0 }}>{icon}</Box>
      <Typography variant="caption" sx={{ flex: 1, color: "text.secondary", fontSize: 11 }}>{label}</Typography>
      {/* Dispositivo donde se fichó (registro del fichaje). */}
      {device !== undefined && (
        <Typography variant="caption" data-testid="clocking-device" sx={{ color: "text.disabled", fontSize: 10 }}>
          {device}
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 11, fontFamily: "monospace" }}>{time}</Typography>
    </Box>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
const EmployeeShiftTracker = () => {
  const { employees, currentClocking, clockings, kioskReturnAfterClocking, clockingDueNotice } = useSelector((s) => s.employees);
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { isEmployeeWebSession } = useClaims();

  const emp = currentClocking
    ? employees.find((e) => e.id === currentClocking.employeeId) ?? null
    : null;

  // Turnos del empleado correspondientes a HOY. Si tiene horario partido
  // (ej. 09-13 y 17-21) aparecen los dos — el modal muestra tabs para
  // alternar entre ellos. Sin esta lista, solo veía el currentClocking
  // (1 solo workItem) y perdía visibilidad del otro turno del día.
  //
  // Filtramos por `start` de HOY para excluir el shift OLVIDADO de días
  // previos que el reducer inyecta como currentClocking (ver comentario
  // en `getEmployeesSuccess`). Ese olvidado NO debe aparecer como tab
  // extra — sólo debe pintarse como fichaje "en curso" para que el
  // empleado lo finalice. Cuando cierre el olvidado, el refresh dispara
  // GetAllEmployees y el currentWorkItem pasa al de hoy real.
  const employeeTodayItems = (clockings || []).filter((c) => {
    if (c.employeeId !== currentClocking?.employeeId) return false;
    const s = c.start ? dayjs.utc(c.start).local() : null;
    return s ? s.isSame(dayjs(), "day") : false;
  });
  const hasSplitShift = employeeTodayItems.length > 1;

  // Todos los shifts abiertos (CheckIn != null && CheckOut == null) de
  // este empleado en la ventana de 4 días que trae el backend. Cada
  // entrada es `{ shift, workItem, isForgotten }` — `isForgotten = true`
  // cuando el WorkScheduleItem NO es de hoy (turno olvidado de un día
  // anterior). El PendingShiftsPanel se muestra si hay CUALQUIER shift
  // olvidado (aunque sea solo 1) o si hay más de 1 abierto en total;
  // así el empleado ve todos los pendientes de una tacada, no solo el
  // más reciente.
  const openShifts = useMemo(() => {
    if (!currentClocking?.employeeId) return [];
    const list = [];
    const today = dayjs();
    for (const wi of clockings || []) {
      if (wi.employeeId !== currentClocking.employeeId) continue;
      const wiStart = wi.start ? dayjs.utc(wi.start).local() : null;
      const isForgotten = wiStart ? !wiStart.isSame(today, "day") : false;
      for (const s of wi.shifts || []) {
        if (s.checkInTime && !s.checkOutTime) {
          list.push({ shift: s, workItem: wi, isForgotten });
        }
      }
    }
    // Más recientes primero.
    list.sort((a, b) => {
      const ta = new Date(a.shift.checkInTime).getTime();
      const tb = new Date(b.shift.checkInTime).getTime();
      return tb - ta;
    });
    return list;
  }, [clockings, currentClocking?.employeeId]);

  const hasForgotten = openShifts.some((o) => o.isForgotten);
  // Mostramos el panel de pendientes cuando:
  //  - hay algún fichaje olvidado (de día previo) — regla principal,
  //    aunque sea 1 solo hay que enseñárselo con opciones de cierre;
  //  - o hay más de un fichaje abierto (por ejemplo dos turnos partidos
  //    del mismo día con dos shifts vivos), para que no se confunda
  //    con cuál está mirando.
  //
  // El empleado puede pinchar "Fichar mi turno de hoy" en el panel para
  // POSPONER los olvidados. `skipPending=true` fuerza el UI normal
  // durante esta apertura del modal — los olvidados seguirán apareciendo
  // en la próxima entrada al modal si no los cerró. La lógica de
  // `selectClockingByIdSuccess` cambia el currentClocking al workItem de
  // hoy (buscado en clockings).
  const [skipPending, setSkipPending] = useState(false);
  const shouldShowPending =
    (hasForgotten || openShifts.length > 1) && !skipPending;

  const [modo, setModo] = useState("inactivo");
  const [timer, setTimer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);

  const handleSkipPending = () => {
    // Buscamos un workItem del empleado que sea DE HOY (no un olvidado).
    // El reducer prioriza en getCurrentClockingSuccess pero como la
    // regla nº 1 es "shift abierto primero" y aquí HAY shifts abiertos,
    // nos toca a nosotros forzar el switch al de hoy.
    const todayItem = employeeTodayItems.find(
      (wi) => !(wi.shifts || []).some((s) => s.checkInTime && !s.checkOutTime),
    ) || employeeTodayItems[0];
    if (todayItem) {
      dispatch(selectClockingByIdSuccess(todayItem.id));
    }
    setSkipPending(true);
  };

  useEffect(() => {
    if (currentClocking?.currentShift) {
      setModo(currentClocking.currentBreak ? "descanso" : "trabajando");
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    } else {
      setModo("inactivo");
      setTimer(null);
    }
  }, [currentClocking]);

  const tick = () => {
    if (!currentClocking?.currentShift) return;
    const base = currentClocking.currentBreak
      ? fromUtc(currentClocking.currentBreak.breakStart)
      : fromUtc(currentClocking.currentShift.checkInTime);
    const t = dayjs.duration(dayjs().diff(base));
    const totalHours = Math.floor(t.asHours());
    setTimer(`${String(totalHours).padStart(2,"0")}:${String(t.minutes()).padStart(2,"0")}:${String(t.seconds()).padStart(2,"0")}`);
  };

  const registrarEvento = async (tipo) => {
    setLoading(true);
    try {
      const res = await axios.post("employeeShift", buildShiftAction({
        employeeId: currentClocking?.employeeId,
        workScheduleItemId: currentClocking?.id,
        type: tipo,
        shiftId: currentClocking?.currentShift?.id,
        breakId: currentClocking?.currentBreak?.id,
      }));
      dispatch(updateClockingSuccess(res.data));
      MyAutoSnack("success", "Evento registrado", enqueueSnackbar, closeSnackbar);

      // After closing a shift, re-fetch employees so currentWorkItem
      // switches from the old (now closed) item to today's item.
      // Sin id: `GetAllEmployees(id)` además hace `setEmployee(id)` y dejaba
      // como empleado activo de la tablet al que acababa de fichar.
      if (tipo === EventoTipo.STOP_SHIFT) {
        dispatch(GetAllEmployees());
      }

      handleClose();
    } catch (err) {
      // El backend devuelve BadRequestException con el motivo cuando el
      // fichaje está restringido al device principal (Sprint 2 · toggle
      // `RestrictClockingToPrimaryDevice`). Sacamos el mensaje real del
      // response para que el empleado sepa qué hacer, si viene; si no,
      // caemos al genérico.
      const backendMsg =
        err?.response?.data?.message
        || err?.response?.data
        || (typeof err?.message === "string" ? err.message : null);
      const msg =
        typeof backendMsg === "string" && backendMsg.length > 0 && backendMsg.length < 240
          ? backendMsg
          : "Error al registrar evento";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Al cerrar el modal hacemos SIEMPRE un refresh + reselect del
  // currentClocking. Sin esto, si el empleado cerró un fichaje olvidado
  // desde el panel y salió, el `state.currentClocking` seguía apuntando
  // al shift ya cerrado — al reabrir, el modal mostraba el fichaje viejo
  // como si estuviera en curso ("tiene memoria"). El refresh sincroniza
  // `state.clockings` con la BD y `getCurrentClockingSuccess` recomputa
  // el foco natural sobre los datos nuevos.
  // `reopenKiosk`: si se fichó desde un kiosko forzado (nadie en la tablet),
  // al cerrar se vuelve a él — fichar no deja a nadie identificado.
  const handleClose = (reopenKiosk = true) => {
    const empId = currentClocking?.employeeId;
    dispatch(changeOpenClockingModal(false));
    if (reopenKiosk !== false && kioskReturnAfterClocking) {
      dispatch(setKioskReturnAfterClocking(false));
      dispatch(openKiosk({ manual: false }));
    }
    if (empId) {
      // Fire-and-forget: para el usuario el modal se cierra ya, y el
      // refresh se completa en background para que el próximo `open`
      // arranque con datos frescos.
      // Sin id: refrescar NO es cambiar de empleado (ver registrarEvento).
      Promise.resolve(dispatch(GetAllEmployees())).then(() => {
        dispatch(getCurrentClockingSuccess(empId));
      });
    }
  };

  const meta = MODO_META[modo];
  const av = createAvatar(emp?.name ?? "?");
  const allShifts = currentClocking?.shifts ?? [];
  const currentShift = allShifts.find((s) => !s.checkOutTime);
  const pastShifts = allShifts.filter((s) => s.checkOutTime);
  const hasHistory = currentShift || pastShifts.length > 0;

  // Detect if the workScheduleItem is from a previous day (unclosed shift)
  const scheduleStart = fromUtc(currentClocking?.start);
  const scheduleEnd = fromUtc(currentClocking?.end);
  const isOldShift = scheduleStart
    ? !scheduleStart.isSame(dayjs(), "day")
    : false;

  // ── Reglas para permitir iniciar el turno seleccionado ─────────
  // Reusamos la lista `openShifts` calculada arriba (todos los shifts
  // del empleado con CheckInTime != null && CheckOutTime == null en
  // la ventana de 4 días que trae el backend). Si esa lista incluye
  // AL MENOS un shift DE HOY o de un día previo → hay fichaje activo
  // y bloqueamos iniciar otro. `hasAnyOpenShift` cubre también los
  // olvidados: si el empleado saltó los olvidados con "Fichar mi
  // turno de hoy", no debe poder abrir un segundo shift mientras el
  // antiguo siga sin cerrar.
  const ALLOW_START_BEFORE_MIN = 15;
  const nowLocal = dayjs();
  const earliestStart = scheduleStart
    ? scheduleStart.clone().subtract(ALLOW_START_BEFORE_MIN, "minute")
    : null;
  const hasAnyOpenShift = openShifts.length > 0;
  const isTooEarly = earliestStart ? nowLocal.isBefore(earliestStart) : false;
  // Turno ya terminado: si la hora actual pasa del final programado, no
  // se puede iniciar. Los olvidados/retroactivos se gestionan por el flujo
  // dedicado de "Correcciones de fichaje" — no queremos que se abran
  // shifts nuevos para un turno cerrado.
  const isTooLate = scheduleEnd ? nowLocal.isAfter(scheduleEnd) : false;
  const canStartShift =
    !hasAnyOpenShift && !isTooEarly && !isTooLate && !!scheduleStart;

  // Copy contextual del hint bajo el botón. Prioridad:
  //   1. Ya tiene un shift abierto → decírselo (no se ve el botón normal
  //      porque `modo` sería "trabajando" en ese caso pero cubrimos el
  //      caso de cambio de tab a otro turno sin shift abierto propio).
  //   2. Aún no ha llegado la ventana de 15 min antes → hora exacta.
  //   3. Turno ya terminado → dirigir a Correcciones de fichaje.
  //   4. Todo OK → sin hint.
  const startBlockedReason = hasAnyOpenShift
    ? "Ya tienes otro fichaje abierto — finalízalo antes de iniciar otro."
    : isTooEarly
      ? `Puedes fichar entrada a partir de las ${earliestStart.format("HH:mm")}.`
      : isTooLate
        ? `Este turno ya ha terminado (${scheduleEnd.format("HH:mm")}). Solicita una corrección de fichaje al admin.`
        : null;

  // ── Vista de fichajes sin finalizar ─────────────────────────────
  // Cuando el empleado tiene fichajes olvidados (o >1 abierto), en
  // lugar del UI normal mostramos la lista con "Finalizar ahora" /
  // "Ajustar hora" por cada uno. Al cerrar todos, el panel llama
  // onAllClosed() y volvemos al UI normal (o cerramos el modal si
  // no queda nada más que hacer).
  if (shouldShowPending && emp) {
    return (
      <DialogAnimate open onClose={handleClose} width="sm">
        <DialogContent style={{ padding: 0 }}>
          <PendingShiftsPanel
            employee={emp}
            openShifts={openShifts}
            onAllClosed={handleClose}
            onClose={handleClose}
            // Sólo damos la opción "posponer y fichar hoy" si el empleado
            // realmente tiene un turno programado hoy. Si no lo tiene,
            // sería un botón sin destino.
            onSkip={
              employeeTodayItems.length > 0 ? handleSkipPending : undefined
            }
          />
        </DialogContent>
      </DialogAnimate>
    );
  }

  return (
    <DialogAnimate open onClose={handleClose} width="md">
      <DialogContent style={{ padding: 0 }}>
        {/* 2026-10-07 · Abierto solo al identificarse porque toca fichar:
            explica el motivo («Tu turno empezó a las 16:00»…). */}
        {clockingDueNotice && (
          <Alert severity="info" data-testid="clocking-due-notice" sx={{ borderRadius: 0 }}>
            {clockingDueNotice}
          </Alert>
        )}
        <Box sx={{ display: "flex", minHeight: 480 }}>

          {/* ── Panel izquierdo: acciones ── */}
          <Box
            sx={{
              flex: "0 0 340px",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(145deg, #f0f4ff 0%, #fafafa 50%, #f5f0ff 100%)",
              backgroundImage: "radial-gradient(circle, rgba(99,102,241,0.08) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* color blobs */}
            <Box sx={{ position: "absolute", top: -40, left: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(99,102,241,0.06)", filter: "blur(30px)", pointerEvents: "none" }} />
            <Box sx={{ position: "absolute", bottom: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(168,85,247,0.07)", filter: "blur(25px)", pointerEvents: "none" }} />

            {/* Header */}
            <Box
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 2.5, py: 1.5,
                borderBottom: "1px solid rgba(99,102,241,0.1)",
                bgcolor: "rgba(255,255,255,0.55)",
                backdropFilter: "blur(8px)",
                position: "relative", zIndex: 1,
              }}
            >
              {!isEmployeeWebSession ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    // Fichar a otro empleado: vuelve al kiosko en modo fichar,
                    // forzado o no según de dónde se vino.
                    const forced = kioskReturnAfterClocking;
                    handleClose(false);
                    dispatch(openKiosk({ intent: "clocking", manual: !forced }));
                  }}
                  sx={{
                    border: "none", bgcolor: "transparent", cursor: "pointer",
                    color: "primary.main", fontWeight: 600, fontSize: 12,
                    fontFamily: "inherit", display: "flex", alignItems: "center", gap: 0.5,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  ← Cambiar usuario
                </Box>
              ) : (
                <Box />
              )}
              <Box
                component="button"
                type="button"
                onClick={handleClose}
                sx={{
                  border: "1.5px solid rgba(99,102,241,0.2)", borderRadius: 2,
                  px: 1.5, py: 0.5, bgcolor: "transparent", cursor: "pointer",
                  fontWeight: 600, fontSize: 12, color: "text.secondary", fontFamily: "inherit",
                  "&:hover": { bgcolor: "rgba(99,102,241,0.06)" },
                }}
              >
                Cerrar
              </Box>
            </Box>

            {/* Tabs por turno cuando hay horario partido (>1 workItem hoy).
                Click cambia el currentClocking y todo el panel se rerenderiza
                con el nuevo turno (jornada, historial, acciones). El badge
                indica el estado de cada turno: ● activo si shift abierto,
                ✓ cerrado si tiene checkOutTime, vacío si aún no se ha fichado. */}
            {hasSplitShift && (
              <Box sx={{ px: 2, pt: 1, position: "relative", zIndex: 1 }}>
                <Tabs
                  value={currentClocking?.id ?? false}
                  onChange={(_, id) => dispatch(selectClockingByIdSuccess(id))}
                  variant="fullWidth"
                  sx={{
                    minHeight: 36,
                    "& .MuiTab-root": {
                      minHeight: 36,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 12,
                    },
                  }}
                >
                  {employeeTodayItems.map((wi) => {
                    const s = fromUtc(wi.start);
                    const e = fromUtc(wi.end);
                    const hasOpenShift = (wi.shifts || []).some(
                      (sh) => sh.checkInTime && !sh.checkOutTime,
                    );
                    const allClosed = (wi.shifts || []).length > 0 && !hasOpenShift;
                    return (
                      <Tab
                        key={wi.id}
                        value={wi.id}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {hasOpenShift && (
                              <FiberManualRecordIcon sx={{ fontSize: 9, color: "success.main" }} />
                            )}
                            {allClosed && (
                              <Box component="span" sx={{ fontSize: 11, color: "text.disabled" }}>
                                ✓
                              </Box>
                            )}
                            <Box component="span">
                              {s?.format("HH:mm")}–{e?.format("HH:mm")}
                            </Box>
                          </Box>
                        }
                      />
                    );
                  })}
                </Tabs>
              </Box>
            )}

            {/* Empleado + estado */}
            <Box
              sx={{
                display: "flex", flexDirection: "column", alignItems: "center",
                pt: 3.5, pb: 2.5, px: 3, gap: 1.5,
                position: "relative", zIndex: 1,
              }}
            >
              <Avatar
                src={emp?.avatar}
                alt={emp?.name}
                sx={{
                  width: 80, height: 80, bgcolor: av.color, fontSize: 30, fontWeight: 700,
                  boxShadow: `0 6px 24px ${av.color}55`,
                }}
              >
                {av.name}
              </Avatar>

              <Typography variant="h6" fontWeight={700}>{emp?.name ?? "Empleado"}</Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1.8, py: 0.6, borderRadius: 10, bgcolor: meta.bg }}>
                <FiberManualRecordIcon sx={{ fontSize: 9, color: meta.color }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: meta.color, fontSize: 12 }}>{meta.label}</Typography>
              </Box>

              {/* Scheduled hours */}
              {scheduleStart && scheduleEnd && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, fontWeight: 500 }}>
                  {(() => {
                    const s = scheduleStart;
                    const e = scheduleEnd;
                    const sameDay = s.isSame(e, "day");
                    const isToday = s.isSame(dayjs(), "day");
                    if (sameDay && isToday) return `Hoy ${s.format("HH:mm")} — ${e.format("HH:mm")}`;
                    if (sameDay) return `${s.format("ddd D MMM")} · ${s.format("HH:mm")} — ${e.format("HH:mm")}`;
                    return `${s.format("ddd D")} ${s.format("HH:mm")} — ${e.format("ddd D")} ${e.format("HH:mm")}`;
                  })()}
                </Typography>
              )}

              {timer && (
                <Typography
                  sx={{
                    fontFamily: "monospace", fontSize: 38, fontWeight: 800, letterSpacing: 3,
                    color: meta.color, lineHeight: 1, mt: 0.5,
                  }}
                >
                  {timer}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mx: 2.5, borderColor: "rgba(99,102,241,0.1)" }} />

            {/* Warning: unclosed shift from a previous day */}
            {isOldShift && currentShift && (
              <Box
                sx={{
                  mx: 2.5, mt: 1.5, px: 2, py: 1.2,
                  borderRadius: 2,
                  bgcolor: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.2)",
                }}
              >
                <Typography variant="caption" fontWeight={700} color="error.main" display="block">
                  Turno sin cerrar — {scheduleStart?.format("dddd D MMM")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Fichado a las {fromUtc(currentShift.checkInTime)?.format("HH:mm")}. Finaliza el turno para continuar.
                </Typography>
              </Box>
            )}

            {/* Botones de acción */}
            <Box
              sx={{
                px: 2.5, py: 2.5, display: "flex", flexDirection: "column", gap: 1.5,
                flex: 1, justifyContent: "center", position: "relative", zIndex: 1,
              }}
            >
              {modo === "inactivo" && (
                <>
                  <LoadingButton
                    fullWidth size="large" variant="contained" color="success"
                    startIcon={<PlayArrowIcon />} loading={loading}
                    disabled={!canStartShift}
                    onClick={() => registrarEvento(EventoTipo.START_SHIFT)}
                    sx={{
                      borderRadius: 3, py: 1.5, fontSize: 15, fontWeight: 700,
                      boxShadow: canStartShift ? "0 4px 14px rgba(22,163,74,0.35)" : "none",
                    }}
                  >
                    Iniciar turno
                  </LoadingButton>
                  {startBlockedReason && (
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "center",
                        color: hasAnyOpenShift ? "error.main" : "warning.main",
                        fontWeight: 600,
                        px: 1,
                      }}
                    >
                      {startBlockedReason}
                    </Typography>
                  )}
                </>
              )}
              {modo === "trabajando" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <LoadingButton
                    fullWidth size="large" variant="contained" color="warning"
                    startIcon={<PauseIcon />} loading={loading}
                    onClick={() => registrarEvento(EventoTipo.START_BREAK)}
                    sx={{ borderRadius: 3, py: 1.4, fontWeight: 700, boxShadow: "0 4px 14px rgba(217,119,6,0.3)" }}
                  >
                    Iniciar descanso
                  </LoadingButton>
                  <LoadingButton
                    fullWidth size="large" variant="contained" color="error"
                    startIcon={<StopIcon />} loading={loading}
                    onClick={() => registrarEvento(EventoTipo.STOP_SHIFT)}
                    sx={{ borderRadius: 3, py: 1.4, fontWeight: 700, boxShadow: "0 4px 14px rgba(239,68,68,0.3)" }}
                  >
                    Finalizar turno
                  </LoadingButton>
                </Box>
              )}
              {modo === "descanso" && (
                <LoadingButton
                  fullWidth size="large" variant="contained" color="primary"
                  startIcon={<RefreshIcon />} loading={loading}
                  onClick={() => registrarEvento(EventoTipo.STOP_BREAK)}
                  sx={{ borderRadius: 3, py: 1.5, fontSize: 15, fontWeight: 700, boxShadow: "0 4px 14px rgba(99,102,241,0.35)" }}
                >
                  Reanudar turno
                </LoadingButton>
              )}

              {/* Solicitar corrección al admin (olvidé cerrar / horas mal /
                  olvidé fichar todo). La propuesta queda PENDING_ADMIN_APPROVAL
                  y el admin la revisa desde AlertsCenter → tab Correcciones.
                  Visible siempre que haya workItem seleccionado, independiente
                  del modo (incluso sin shifts → modo "crear retroactivo"). */}
              {currentClocking && (
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setCorrectionOpen(true)}
                  sx={{
                    mt: 0.5,
                    fontWeight: 600,
                    fontSize: 12,
                    color: "text.secondary",
                    textTransform: "none",
                    "&:hover": { color: "primary.main", bgcolor: "rgba(99,102,241,0.06)" },
                  }}
                >
                  ¿Hubo un error? Solicitar corrección
                </Button>
              )}
            </Box>
          </Box>

          {/* ── Panel derecho: historial ── */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid rgba(99,102,241,0.1)",
              bgcolor: "rgba(255,255,255,0.8)",
            }}
          >
            {/* Header historial */}
            <Box
              sx={{
                px: 2.5, py: 1.5,
                borderBottom: "1px solid rgba(99,102,241,0.08)",
                display: "flex", alignItems: "center", gap: 1,
                bgcolor: "rgba(248,250,255,0.9)",
              }}
            >
              <HistoryIcon sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>
                {isOldShift ? `Historial — ${scheduleStart?.format("DD/MM")}` : "Historial de hoy"}
              </Typography>
              {hasHistory && (
                <Chip
                  label={pastShifts.length + (currentShift ? 1 : 0)}
                  size="small"
                  sx={{ ml: "auto", height: 18, fontSize: 10, fontWeight: 700, bgcolor: "rgba(99,102,241,0.1)", color: "primary.main" }}
                />
              )}
            </Box>

            {/* Lista jornadas */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
              {!hasHistory ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 1.5, opacity: 0.4 }}>
                  <AccessTimeIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                  <Typography variant="body2" color="text.disabled" fontWeight={500}>
                    Sin jornadas hoy
                  </Typography>
                </Box>
              ) : (
                <>
                  {currentShift && <JornadaTimeline jornada={currentShift} />}
                  {pastShifts.map((j, i) => <JornadaTimeline key={i} jornada={j} />)}
                </>
              )}
            </Box>
          </Box>

        </Box>
      </DialogContent>

      {correctionOpen && currentClocking && emp && (
        <EmployeeCorrectionDialog
          open={correctionOpen}
          onClose={() => setCorrectionOpen(false)}
          workItem={currentClocking}
          employee={emp}
        />
      )}
    </DialogAnimate>
  );
};

export default EmployeeShiftTracker;
